import React, { useState, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, TrendingUp, Calculator, Target, Zap, DollarSign, PiggyBank, Heart, LayoutGrid, Download, FolderOpen, Building2 } from 'lucide-react';
import { generateRealTimeLoanPrograms } from './utils/realTimeLoanPrograms';
import { calculateLoanProgramValues } from './utils/mortgageUtils';
import { AnalyticsView } from './components/AnalyticsView';
import { OptimizationSuggestions } from './components/OptimizationSuggestions';
import { LoanProgramComparison } from './components/LoanProgramComparison';
import { PersonalizedFinancialGoals } from './components/PersonalizedFinancialGoals';
import { BudgetImpactAnalyzer } from './components/BudgetImpactAnalyzer';
import { LoanProgramRecommender } from './components/LoanProgramRecommender';
import { SavingsGoalCalculator } from './components/SavingsGoalCalculator';
import { CreditScoreSimulator } from './components/CreditScoreSimulator';
import { FinancialHealthDashboard } from './components/FinancialHealthDashboard';
import { ScenarioComparison } from './components/ScenarioComparison';
import { SavedScenariosManager } from './components/SavedScenariosManager';
import { EscrowTimeline } from './components/EscrowTimeline';
import { SupremeIntelligencePDFView } from './components/SupremeIntelligencePDFView';
import { useSupremeIntelligencePDFGeneration } from '@/hooks/useSupremeIntelligencePDFGeneration';
import type { MortgageData } from '../CalculatorTabs';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';

interface SupremeIntelligenceTabProps {
  mortgageData: MortgageData;
  formData?: EnhancedUnifiedFormData;
}

export const SupremeIntelligenceTab: React.FC<SupremeIntelligenceTabProps> = ({ mortgageData, formData }) => {
  const [selectedTerm, setSelectedTerm] = useState('30');
  const [activeTab, setActiveTab] = useState(formData ? 'health' : 'analytics');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);
  const { generateSupremeIntelligencePDF, isGenerating } = useSupremeIntelligencePDFGeneration();

  const loanPrograms = generateRealTimeLoanPrograms(mortgageData);
  const termYears = parseInt(selectedTerm);
  const calculatedPrograms = loanPrograms.map(program => 
    calculateLoanProgramValues(program, termYears, mortgageData)
  );

  const handleExportPDF = async () => {
    await generateSupremeIntelligencePDF(pdfRef, formData);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-400" />
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Supreme Intelligence
          </h2>
          <Sparkles className="w-8 h-8 text-purple-400" />
        </div>
        <p className="text-slate-400 text-lg">
          Advanced AI-powered mortgage analytics and optimization
        </p>
        {formData && (
          <Button
            onClick={handleExportPDF}
            disabled={isGenerating}
            variant="outline"
            className="mt-4 gap-2"
          >
            <Download className="w-4 h-4" />
            {isGenerating ? 'Generating PDF...' : 'Export Full Analysis as PDF'}
          </Button>
        )}
      </div>

      <div className="flex justify-center">
        <div className="flex items-center gap-3">
          <span className="text-slate-300 font-medium">Loan Term:</span>
          <ToggleGroup type="single" value={selectedTerm} onValueChange={(value) => value && setSelectedTerm(value)}>
            <ToggleGroupItem value="15" className="data-[state=on]:bg-purple-600">15 Years</ToggleGroupItem>
            <ToggleGroupItem value="20" className="data-[state=on]:bg-purple-600">20 Years</ToggleGroupItem>
            <ToggleGroupItem value="30" className="data-[state=on]:bg-purple-600">30 Years</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-800/50 border border-slate-700 flex-wrap justify-start">
          {formData && <TabsTrigger value="health"><Heart className="w-4 h-4 mr-2" />Financial Health</TabsTrigger>}
          {formData && <TabsTrigger value="comparison"><LayoutGrid className="w-4 h-4 mr-2" />Compare Scenarios</TabsTrigger>}
          <TabsTrigger value="analytics"><TrendingUp className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
          <TabsTrigger value="programs"><Calculator className="w-4 h-4 mr-2" />Loan Programs</TabsTrigger>
          <TabsTrigger value="budget"><DollarSign className="w-4 h-4 mr-2" />Budget Impact</TabsTrigger>
          <TabsTrigger value="savings"><PiggyBank className="w-4 h-4 mr-2" />Savings Goals</TabsTrigger>
          <TabsTrigger value="credit"><TrendingUp className="w-4 h-4 mr-2" />Credit Simulator</TabsTrigger>
          <TabsTrigger value="escrow"><Building2 className="w-4 h-4 mr-2" />Escrow Timeline</TabsTrigger>
          <TabsTrigger value="optimization"><Zap className="w-4 h-4 mr-2" />Optimization</TabsTrigger>
        </TabsList>

        {formData && (
          <TabsContent value="health" className="mt-6">
            <FinancialHealthDashboard formData={formData} mortgagePayment={mortgageData.monthlyPayment.total} />
          </TabsContent>
        )}

        {formData && (
          <TabsContent value="comparison" className="mt-6">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setManageDialogOpen(true)}
                  className="gap-2"
                >
                  <FolderOpen className="w-4 h-4" />
                  Manage Saved Scenarios
                </Button>
              </div>
              <ScenarioComparison formData={formData} />
            </div>
          </TabsContent>
        )}

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsView selectedTerm={selectedTerm} mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="programs" className="mt-6">
          {formData ? (
            <LoanProgramRecommender formData={formData} baseMonthlyPayment={mortgageData.monthlyPayment.total} />
          ) : (
            <LoanProgramComparison programs={calculatedPrograms} mortgageData={mortgageData} selectedTerm={selectedTerm} />
          )}
        </TabsContent>

        <TabsContent value="budget" className="mt-6">
          {formData ? (
            <BudgetImpactAnalyzer formData={formData} mortgagePayment={mortgageData.monthlyPayment.total} />
          ) : (
            <Card className="p-6"><p className="text-muted-foreground">Complete the calculator to see budget impact analysis</p></Card>
          )}
        </TabsContent>

        <TabsContent value="savings" className="mt-6">
          {formData ? (
            <SavingsGoalCalculator formData={formData} estimatedMonthlyPayment={mortgageData.monthlyPayment.total} />
          ) : (
            <Card className="p-6"><p className="text-muted-foreground">Complete the calculator to see savings goals</p></Card>
          )}
        </TabsContent>

        <TabsContent value="credit" className="mt-6">
          {formData ? (
            <CreditScoreSimulator formData={formData} />
          ) : (
            <Card className="p-6"><p className="text-muted-foreground">Complete the calculator to simulate credit improvements</p></Card>
          )}
        </TabsContent>

        <TabsContent value="escrow" className="mt-6">
          <EscrowTimeline formData={formData} mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="optimization" className="mt-6">
          <div className="space-y-6">
            <OptimizationSuggestions selectedTerm={selectedTerm} mortgageData={mortgageData} />
            <PersonalizedFinancialGoals mortgageData={mortgageData} selectedTerm={selectedTerm} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Hidden PDF View */}
      {formData && (
        <div ref={pdfRef}>
          <SupremeIntelligencePDFView
            formData={formData}
            // TODO: Pass actual data from components when available
          />
        </div>
      )}

      {/* Saved Scenarios Manager */}
      <SavedScenariosManager
        open={manageDialogOpen}
        onOpenChange={setManageDialogOpen}
      />
    </div>
  );
};
